/**
 * A small drawn mark per format.
 *
 * Line drawings rather than emoji: the type here is Lato and the numerals are
 * tabular, and a colour emoji dropped into that reads as somebody else's
 * artwork. These inherit `currentColor`, so a format that is out picks up the
 * "go and get it" colour without the icon set knowing anything about themes.
 */

import type { DateSlot, Medium } from '../../poller/types.js';
import { formatLabel } from '../lib/formats.js';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function paths(slot: DateSlot, medium: Medium) {
  if (slot === 'audiobook') {
    // Headphones.
    return (
      <>
        <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
        <rect x="2.5" y="13.5" width="4" height="6" rx="1.6" />
        <rect x="17.5" y="13.5" width="4" height="6" rx="1.6" />
      </>
    );
  }

  if (slot === 'ebook') {
    // A screen with a line of text.
    return (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 8h6M9 12h6M9 16h3" />
      </>
    );
  }

  if (slot === 'paperback') {
    // A book with a curled cover — the soft one.
    return (
      <>
        <path d="M5 5.5c2.5-1.5 5.5-1.5 7 0v13c-1.5-1.5-4.5-1.5-7 0z" />
        <path d="M19 5.5c-2.5-1.5-5.5-1.5-7 0v13c1.5-1.5 4.5-1.5 7 0z" />
      </>
    );
  }

  if (slot === 'streaming') {
    // Play.
    return (
      <>
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M10.5 8.5l4 2.5-4 2.5z" />
        <path d="M8 21h8" />
      </>
    );
  }

  if (slot === 'physical') {
    // A disc.
    return (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="2" />
      </>
    );
  }

  if (slot === 'digital') {
    // Download.
    return (
      <>
        <path d="M12 3v11" />
        <path d="M8 10.5l4 4 4-4" />
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
      </>
    );
  }

  // primary — different object per medium: a hardback, a cinema screen, an
  // aerial. The slot is the same; what arrives is not.
  if (medium === 'book') {
    return (
      <>
        <path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2z" />
        <path d="M7 20a2 2 0 0 1 2-2h9" />
        <path d="M9 4v14" />
      </>
    );
  }

  if (medium === 'tv') {
    return (
      <>
        <path d="M12 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
        <path d="M7.5 15.5a6 6 0 0 1 0-8M16.5 7.5a6 6 0 0 1 0 8" />
        <path d="M12 13v8" />
      </>
    );
  }

  return (
    <>
      <rect x="3" y="5" width="18" height="12" rx="1.5" />
      <path d="M7 21l5-4 5 4" />
    </>
  );
}

export function FormatIcon({
  slot,
  medium,
  size = 15,
}: {
  slot: DateSlot;
  medium: Medium;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={formatLabel(slot, medium)}
      {...STROKE}
    >
      {paths(slot, medium)}
    </svg>
  );
}
